// src/ProtectedComponent.js
import React, { useContext } from 'react';
import { AuthContext } from './context/AuthContext';
import './styles/ProtectedComponent.css';

/**
 * ProtectedComponent - Wrapper for function-based access control
 *
 * Usage:
 *   <ProtectedComponent functionCode="OP01" functionName="License Management">
 *     <YourComponent />
 *   </ProtectedComponent>
 *
 * Access Rules:
 * - Super Admins: Always have access (no function check)
 * - Pending users: No access (must be approved first)
 * - Regular users: Must have the specific function code in allowedFunctions array
 */
const ProtectedComponent = ({
  functionCode,
  functionCodes,          // optional: any-of list; used by pages whose access is a family of capability codes
  functionName,
  children,
  showBadge = true
}) => {
  const { user } = useContext(AuthContext);

  // Not logged in
  if (!user) {
    return (
      <div className="protected-component-denied">
        <div className="access-denied-box">
          <h2>Authentication Required</h2>
          <p>Please log in to access this feature.</p>
        </div>
      </div>
    );
  }

  // User is pending approval
  if (user.role === 'pending') {
    return (
      <div className="protected-component-denied">
        <div className="access-denied-box pending">
          <h2>Pending Approval</h2>
          <p>Your account is pending approval by a system administrator.</p>
          <p className="pending-message">
            You will receive access once your account has been reviewed and approved.
          </p>
        </div>
      </div>
    );
  }

  // Super admins always have access
  if (user.isSuperAdmin) {
    return (
      <div className="protected-component">
        {showBadge && functionCode && (
          <div className="function-badge super-admin">
            <span className="badge-code">{functionCode}</span>
            <span className="badge-name">{functionName}</span>
          </div>
        )}
        {children}
      </div>
    );
  }

  // Check if user has the required function (or is authorized for all functions)
  const codes = Array.isArray(functionCodes) && functionCodes.length ? functionCodes : [functionCode];
  const hasAccess = user.authorizedForAll || codes.some((c) => user.allowedFunctions?.includes(c));

  if (!hasAccess) {
    return (
      <div className="protected-component-denied">
        <div className="access-denied-box">
          <h2>Access Denied</h2>
          <p>
            You do not have permission to access <strong>{functionName}</strong> ({functionCode}).
          </p>
          <p className="help-message">
            Please contact your system administrator if you believe you should have access.
          </p>
        </div>
      </div>
    );
  }

  // User has access
  return (
    <div className="protected-component">
      {showBadge && functionCode && (
        <div className="function-badge">
          <span className="badge-code">{functionCode}</span>
          <span className="badge-name">{functionName}</span>
        </div>
      )}
      {children}
    </div>
  );
};

export default ProtectedComponent;
